import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { helpContent } from '../components/helpContent';
import { htmlToText, pageToText } from './htmlToText';
import { MODULE_LABELS, TAB_NAMES, helpTopicKey, isInternalTopic } from '../utils/menuMap';

/** Every in-app help topic as plain text, in the order they are written: [{ key, title, text }]. */
export function helpTopics() {
  return Object.entries(helpContent).map(([key, topic]) => ({
    key,
    title: topic.title,
    text: htmlToText(renderToStaticMarkup(<div>{topic.content}</div>)),
  }));
}

/**
 * The menu for the help text: the side menu's modules and each module's tabs, from utils/menuMap.js. System Admin and Tech Support are left out:
 * account managers never see it, and for customers so is Account Management.
 */
export function assistantMenu(audience = 'partner') {
  const modules = {};
  const tabs = {};
  Object.keys(TAB_NAMES).forEach((path) => {
    if (path === '/system-admin' || path === '/tech-support') return;
    // Customers' users have no Account Management: it is the account managers' own screen
    if (audience === 'customer' && path === '/account-management') return;
    modules[path] = MODULE_LABELS[path];
    tabs[path] = TAB_NAMES[path];
  });
  return { modules, tabs };
}

/** The screens a help topic is about, as menu paths: every module that has a section whose help topic this is. */
export function wherePaths(key, menu) {
  const paths = [];
  Object.keys(menu.tabs).forEach((path) => {
    Object.keys(menu.tabs[path]).forEach((section) => {
      if (helpTopicKey(path, section) === key) paths.push(`${menu.modules[path]} > ${menu.tabs[path][section]}`);
    });
  });
  return paths;
}

/** The same screens as addresses to open: [{ label: 'Accounts > Import / Export', to: '/accounts/import-export', module: 'accounts' }]. */
export function whereLinks(key, menu) {
  const links = [];
  Object.keys(menu.tabs).forEach((path) => {
    Object.keys(menu.tabs[path]).forEach((section) => {
      if (helpTopicKey(path, section) === key) {
        links.push({ label: `${menu.modules[path]} > ${menu.tabs[path][section]}`, to: `${path}/${section}`, module: path.slice(1) });
      }
    });
  });
  return links;
}

/**
 * The text the help assistant answers from, one file: the help topics, the getting-started guide and the account manager
 * guides. Written to backend/src/main/resources/assistant/knowledge.md by knowledge.test.js.
 */
export function buildKnowledge({ gettingStartedHtml, guides, audience = 'partner' }) {
  const customer = audience === 'customer';
  const menu = assistantMenu(audience);
  const parts = [
    '<!-- Generated by frontend/src/assistant/knowledge.test.js: do not edit. Regenerate with UPDATE_HELP_CORPUS=1 npm test -->',
    '# Where to find each screen\n\nThe side menu has these modules, and each module has these tabs. A tab is opened by clicking '
      + 'its name inside the module.\n\n'
      + Object.keys(menu.tabs).map((path) => `- ${menu.modules[path]}: ${Object.values(menu.tabs[path]).join(', ')}`).join('\n'),
    '# In-app help, one topic per screen',
  ];
  helpTopics().filter((t) => !isInternalTopic(t.key) && !(customer && t.key.startsWith('partner-'))).forEach((t) => {
    const where = wherePaths(t.key, menu);
    parts.push(`## ${t.title} (help topic: ${t.key}${where.length ? `; screen: ${where.join(' or ')}` : ''})\n\n${t.text}`);
  });
  parts.push(`# Getting started guide\n\n${pageToText(gettingStartedHtml)}`);
  // The sales guides are for account managers only: customers' text never includes them
  if (!customer) {
    Object.entries(guides).forEach(([name, html]) => parts.push(`# Account manager guide: ${name}\n\n${pageToText(html)}`));
  }
  return `${parts.join('\n\n')}\n`;
}
